import React, { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Camera, Upload, Sparkles, ArrowLeft, Plus, Check, X,
  AlertTriangle, Package, Loader2
} from 'lucide-react';
import api from '../utils/api';

const AIScanner = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('jobId');
  
  const fileInputRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [creditsUsed, setCreditsUsed] = useState(0);
  const [creditsRemaining, setCreditsRemaining] = useState(null);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        toast.error('Please select a JPEG, PNG, or WebP image');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image must be smaller than 10MB');
        return;
      }
      
      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      
      // Reset results
      setScanResults(null);
      setSelectedItems([]);
    }
  };

  const handleScan = async () => {
    if (!selectedImage) {
      toast.error('Please select an image first');
      return;
    }
    
    setScanning(true);
    
    try {
      // Convert image to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(',')[1]; // Remove data URL prefix
        
        try {
          const response = await api.post('/inventory/ai-scan', {
            job_id: jobId || '',
            image_base64: base64,
            mime_type: selectedImage.type
          });
          
          setScanResults(response.data.items);
          setCreditsUsed(response.data.credits_used);
          setCreditsRemaining(response.data.credits_remaining);
          setSelectedItems(response.data.items.map((_, idx) => idx)); // Select all by default
          
          toast.success(`Found ${response.data.items.length} items!`);
        } catch (error) {
          if (error.response?.status === 402) {
            toast.error('Insufficient AI credits. Please purchase more credits.');
          } else {
            toast.error(error.response?.data?.detail || 'AI scan failed');
          }
        } finally {
          setScanning(false);
        }
      };
      reader.readAsDataURL(selectedImage);
    } catch (error) {
      toast.error('Failed to process image');
      setScanning(false);
    }
  };

  const toggleItemSelection = (idx) => {
    if (selectedItems.includes(idx)) {
      setSelectedItems(selectedItems.filter(i => i !== idx));
    } else {
      setSelectedItems([...selectedItems, idx]);
    }
  };

  const handleAddSelectedItems = async () => {
    if (!jobId) {
      toast.error('Please select a job first');
      return;
    }
    
    const itemsToAdd = scanResults.filter((_, idx) => selectedItems.includes(idx));
    
    try {
      for (const item of itemsToAdd) {
        await api.post('/inventory', {
          job_id: jobId,
          category: item.category,
          item_name: item.name,
          quantity: item.quantity || 1,
          is_fragile: item.is_fragile,
          notes: item.notes
        });
      }
      
      toast.success(`Added ${itemsToAdd.length} items to inventory`);
      navigate(`/inventory?jobId=${jobId}`);
    } catch (error) {
      toast.error('Failed to add items');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50" data-testid="ai-scanner">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Sparkles className="text-primary" size={24} />
                  AI Inventory Scanner
                </h1>
                <p className="text-sm text-gray-500">Take a photo to identify items</p>
              </div>
            </div>
            {creditsRemaining !== null && (
              <Badge variant="outline" className="text-sm">
                <Sparkles size={14} className="mr-1" />
                {creditsRemaining} credits
              </Badge>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Image Upload Section */}
        <Card>
          <CardHeader>
            <CardTitle>Upload Photo</CardTitle>
            <CardDescription>
              Take or upload a photo of items you want to add to inventory
            </CardDescription>
          </CardHeader>
          <CardContent>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageSelect}
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
            />
            
            {!imagePreview ? (
              <div 
                className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center cursor-pointer hover:border-primary transition-colors"
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-area"
              >
                <Camera size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 font-medium">Click to upload or take a photo</p>
                <p className="text-sm text-gray-500 mt-2">JPEG, PNG, or WebP (max 10MB)</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="relative">
                  <img 
                    src={imagePreview} 
                    alt="Selected" 
                    className="w-full max-h-96 object-contain rounded-lg"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => {
                      setSelectedImage(null);
                      setImagePreview(null);
                      setScanResults(null);
                    }}
                  >
                    <X size={16} className="mr-1" />
                    Remove
                  </Button>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload size={18} className="mr-2" />
                    Change Image
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={handleScan}
                    disabled={scanning}
                    data-testid="scan-btn"
                  >
                    {scanning ? (
                      <>
                        <Loader2 size={18} className="mr-2 animate-spin" />
                        Scanning...
                      </>
                    ) : (
                      <>
                        <Sparkles size={18} className="mr-2" />
                        Scan with AI (1 credit)
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Scanning Progress */}
        {scanning && (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Loader2 size={48} className="mx-auto text-primary animate-spin mb-4" />
                <h3 className="font-semibold text-lg mb-2">Analyzing Image...</h3>
                <p className="text-gray-500">Our AI is identifying items in your photo</p>
                <Progress value={66} className="mt-4 h-2" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Scan Results */}
        {scanResults && (
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Detected Items</CardTitle>
                  <CardDescription>
                    Select items to add to your inventory
                  </CardDescription>
                </div>
                <Badge variant="secondary">
                  {selectedItems.length} of {scanResults.length} selected
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {scanResults.map((item, idx) => (
                  <div
                    key={idx}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      selectedItems.includes(idx) 
                        ? 'border-primary bg-primary/5' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => toggleItemSelection(idx)}
                    data-testid={`result-item-${idx}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold">{item.name}</h4>
                          {item.is_fragile && (
                            <Badge variant="destructive" className="text-xs">
                              <AlertTriangle size={10} className="mr-1" />
                              Fragile
                            </Badge>
                          )}
                          {item.quantity > 1 && (
                            <Badge variant="outline">{item.quantity}x</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{item.category}</p>
                        {item.estimated_dimensions && (
                          <p className="text-xs text-gray-400 mt-1">
                            Dimensions: {item.estimated_dimensions}
                          </p>
                        )}
                        {item.notes && (
                          <p className="text-xs text-orange-600 mt-1">{item.notes}</p>
                        )}
                      </div>
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        selectedItems.includes(idx)
                          ? 'bg-primary border-primary text-white'
                          : 'border-gray-300'
                      }`}>
                        {selectedItems.includes(idx) && <Check size={14} />}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {scanResults.length > 0 && (
                <div className="mt-6 flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setSelectedItems(
                      selectedItems.length === scanResults.length ? [] : scanResults.map((_, i) => i)
                    )}
                  >
                    {selectedItems.length === scanResults.length ? 'Deselect All' : 'Select All'}
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={handleAddSelectedItems}
                    disabled={selectedItems.length === 0 || !jobId}
                    data-testid="add-items-btn"
                  >
                    <Plus size={18} className="mr-2" />
                    Add {selectedItems.length} Item{selectedItems.length !== 1 ? 's' : ''} to Inventory
                  </Button>
                </div>
              )}
              
              {!jobId && (
                <p className="text-sm text-orange-600 mt-4 text-center">
                  Please select a job to add items to inventory
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Cost Info */}
        <Card className="bg-gradient-to-r from-primary/10 to-blue-100/50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                <Sparkles className="text-primary" size={24} />
              </div>
              <div>
                <h3 className="font-semibold">AI-Powered Detection</h3>
                <p className="text-sm text-gray-600">
                  Each scan uses 1 AI credit. Our AI can identify furniture, appliances, 
                  boxes, and estimate dimensions automatically.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AIScanner;
